import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { get, post } from '../lib/api/client'
import type { ApiError } from '../lib/api/client'
import { queryClient } from '../lib/queryClient'

export interface AuthUser {
  id: string
  email?: string
  username?: string
  display_name?: string
  avatar_url?: string | null
}

export interface AuthError { message: string; code?: string; status?: number }

interface AuthContextType {
  user: AuthUser | null
  /**
   * Server-side cookie session indicator. After the cookie-session refactor
   * (2026-05-10) the browser no longer holds Supabase JWTs; this is a
   * presence-only signal so consumers can keep using `if (session)` checks.
   * Kept as `unknown`-shaped object so TS doesn't complain about reads of
   * .access_token on legacy code — those reads return undefined and the
   * call sites that need the JWT have been migrated to backend SSO endpoints.
   */
  session: { exists: true } | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>
  signUp: (email: string, password: string, username: string, displayName: string) => Promise<{ error: AuthError | null }>
  signOut: () => Promise<void>
  authLoading: boolean
  signInWithGoogle: () => Promise<void>
  signInWithX: () => Promise<void>
  completeOAuthLogin: (code: string) => Promise<{
    error: AuthError | null
    needsInvitation?: boolean
    invitationData?: { user_id: string; email: string; token: string }
  }>
  completeOAuthWithToken: (
    accessToken: string,
    invitationToken?: string,
  ) => Promise<{
    error: AuthError | null
    needsInvitation?: boolean
    invitationData?: { user_id: string; email: string; token: string }
  }>
  /**
   * Hydrate auth state from server. Replaces the old refreshToken() — there
   * is no client-side token refresh anymore (the cookie session handles it
   * server-side under a Redis SETNX lock).
   */
  refreshUser: () => Promise<boolean>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [session, setSession] = useState<{ exists: true } | null>(null)
  const [loading, setLoading] = useState(true)
  const [authLoading, setAuthLoading] = useState(true)

  // Pull current user from /api/auth/me. Returns true when a session is alive.
  const fetchMe = useCallback(async (): Promise<boolean> => {
    try {
      const data = await get<{ user: AuthUser }>('/api/auth/me')
      if (data?.user?.id) {
        setUser(data.user)
        setSession({ exists: true })
        return true
      }
      return false
    } catch {
      // 401 / 404 / network — treat as logged out
      setUser(null)
      setSession(null)
      return false
    }
  }, [])

  const refreshUser = useCallback(async (): Promise<boolean> => fetchMe(), [fetchMe])

  // Boot: try to migrate the legacy kommend_refresh_token cookie into a new
  // __session cookie, then hydrate user from /me. Both endpoints are safe to
  // call when the user is logged out — they just return falsy values.
  useEffect(() => {
    let cancelled = false

    const init = async () => {
      try {
        // Best-effort upgrade — failures are silent (user simply isn't migrated).
        try {
          const upgrade = await post<{ upgraded: boolean; user?: AuthUser | null }>(
            '/api/auth/upgrade-session',
            {},
          )
          if (cancelled) return
          if (upgrade?.upgraded && upgrade.user?.id) {
            setUser(upgrade.user)
            setSession({ exists: true })
            setLoading(false)
            return
          }
        } catch {
          // upgrade endpoint not reachable / no legacy cookie — fall through to /me
        }

        if (cancelled) return
        await fetchMe()
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    init()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Listen for client-emitted session-expired event (from api/client.ts on 401).
  useEffect(() => {
    const handleSessionExpired = () => {
      console.warn('[AuthContext] Session expired, clearing local user state')
      setUser(null)
      setSession(null)
      queryClient.clear()
    }

    window.addEventListener('auth:session-expired', handleSessionExpired)
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired)
  }, [])

  const signIn = async (email: string, password: string) => {
    try {
      const data = await post<{ user?: AuthUser }>('/api/auth/login', { email, password })
      const newUser = (data?.user || null) as AuthUser | null
      setUser(newUser)
      setSession(newUser ? { exists: true } : null)
      return { error: null }
    } catch (err: unknown) {
      const apiErr = err as ApiError
      let errorMsg = apiErr.message || '登录失败'
      if (errorMsg.includes('missing_fields')) {
        errorMsg = '请填写邮箱和密码'
      } else if (errorMsg.includes('invalid_credentials') || errorMsg.includes('Invalid login')) {
        errorMsg = '邮箱或密码错误'
      }
      const e: AuthError = { message: errorMsg, code: apiErr.code, status: apiErr.status }
      return { error: e }
    }
  }

  const signUp = async (email: string, password: string, username: string, displayName: string) => {
    try {
      const data = await post<AuthUser>('/api/auth/register', {
        email,
        password,
        username,
        display_name: displayName,
      })

      // Auto login after registration (creates the server-side session).
      try {
        const loginData = await post<{ user?: AuthUser }>('/api/auth/login', { email, password })
        const newUser = (loginData?.user || data || null) as AuthUser | null
        setUser(newUser)
        setSession(newUser ? { exists: true } : null)
      } catch {
        setUser((data || null) as AuthUser | null)
        setSession(null)
      }

      return { error: null }
    } catch (err: unknown) {
      const apiErr = err as ApiError
      const detail = apiErr.message || ''
      let errorMsg = '注册失败'

      if (detail.includes('missing_fields')) {
        errorMsg = '请填写所有必填字段'
      } else if (detail.includes('invalid_username_format')) {
        errorMsg = '用户名只能包含小写字母、数字和下划线'
      } else if (detail.includes('username_length_invalid')) {
        errorMsg = '用户名长度需在3-30个字符之间'
      } else if (detail.includes('password_too_short')) {
        errorMsg = '密码至少需要8个字符'
      } else if (detail.includes('password_missing_uppercase')) {
        errorMsg = '密码必须包含至少一个大写字母'
      } else if (detail.includes('password_missing_lowercase')) {
        errorMsg = '密码必须包含至少一个小写字母'
      } else if (detail.includes('password_missing_number')) {
        errorMsg = '密码必须包含至少一个数字'
      } else if (detail.includes('username_already_exists')) {
        errorMsg = '该用户名已被使用，请换一个'
      } else if (detail.includes('email_already_exists') || detail.includes('email_already_registered')) {
        errorMsg = '该邮箱已被注册'
      } else if (detail.includes('profile_creation_failed')) {
        errorMsg = '创建用户资料失败，请重试'
      } else if (detail.includes('register_failed')) {
        if (detail.includes('already registered') || detail.includes('already exists')) {
          errorMsg = '该邮箱已被注册'
        } else {
          errorMsg = '注册失败，请稍后重试'
        }
      }

      const e: AuthError = { message: errorMsg, code: apiErr.code, status: apiErr.status }
      return { error: e }
    }
  }

  const signOut = async () => {
    try {
      await post<{ ok?: boolean }>('/api/auth/logout', {})
    } catch {
      // ignore network/server errors during logout
    }
    setUser(null)
    setSession(null)
    queryClient.clear()
  }

  const signInWithGoogle = async () => {
    try {
      const redirect = typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : ''
      const data = await get<{ url: string }>(`/api/auth/google_url?redirect=${encodeURIComponent(redirect)}`)
      if (data.url && typeof window !== 'undefined') {
        window.location.href = data.url
      }
    } catch (err) {
      console.error('[AuthContext] Google sign in error:', err)
    }
  }

  const signInWithX = async () => {
    try {
      const redirect = typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : ''
      const data = await get<{ url: string }>(`/api/auth/x_url?redirect=${encodeURIComponent(redirect)}`)
      if (data.url && typeof window !== 'undefined') {
        window.location.href = data.url
      }
    } catch (err) {
      console.error('[AuthContext] X sign in error:', err)
    }
  }

  const completeOAuthLogin = async (code: string) => {
    try {
      const data = await get<{
        user?: AuthUser
        needs_invitation?: boolean
        user_id?: string
        email?: string
        token?: string
      }>(`/api/auth/oauth_callback?code=${encodeURIComponent(code)}`)

      if (data.needs_invitation) {
        return {
          error: null,
          needsInvitation: true,
          invitationData: {
            user_id: data.user_id || '',
            email: data.email || '',
            token: data.token || '',
          },
        }
      }

      const newUser = (data.user || null) as AuthUser | null
      setUser(newUser)
      setSession(newUser ? { exists: true } : null)
      return { error: null }
    } catch (err: unknown) {
      const apiErr = err as ApiError
      const e: AuthError = {
        message: apiErr.message || 'OAuth failed',
        code: apiErr.code,
        status: apiErr.status,
      }
      return { error: e }
    }
  }

  const completeOAuthWithToken = async (
    accessToken: string,
    invitationToken?: string,
  ) => {
    try {
      let url = `/api/auth/complete?access_token=${encodeURIComponent(accessToken)}`
      if (invitationToken) {
        url += `&invitation_token=${encodeURIComponent(invitationToken)}`
      }

      const data = await get<{
        user?: AuthUser
        needs_invitation?: boolean
        user_id?: string
        email?: string
        token?: string
      }>(url)

      if (data.needs_invitation) {
        return {
          error: null,
          needsInvitation: true,
          invitationData: {
            user_id: data.user_id || '',
            email: data.email || '',
            token: accessToken,
          },
        }
      }

      // Backend establishes the server-side session (cookie) inside /complete
      // even for implicit-flow OAuth — the cookie is set on the response, so
      // subsequent requests will carry it via withCredentials. Session expires
      // when the access JWT does (~1h) since implicit flow has no refresh.
      const newUser = (data.user || null) as AuthUser | null
      if (newUser) {
        setUser(newUser)
        setSession({ exists: true })
      }
      return { error: null }
    } catch (err: unknown) {
      const apiErr = err as ApiError
      const e: AuthError = {
        message: apiErr.message || 'OAuth failed',
        code: apiErr.code,
        status: apiErr.status,
      }
      return { error: e }
    }
  }

  // Sync authLoading with loading state
  useEffect(() => {
    setAuthLoading(loading)
  }, [loading])

  const value: AuthContextType = {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
    signInWithGoogle,
    signInWithX,
    completeOAuthLogin,
    completeOAuthWithToken,
    refreshUser,
    authLoading,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
